<template>
  <div class="min-h-screen bg-gradient-to-br from-[#FAFBFF] via-[#F8FAFF] to-[#F0F7FF] dark:from-[#0a0a0a] dark:to-[#1a1a1a] p-6" :dir="isRtl ? 'rtl' : 'ltr'">
    <div class="max-w-7xl mx-auto">
      <form @submit.prevent="saveProduct">
        
        <!-- Page Header -->
        <div class="mb-8">
          <div class="bg-white/80 dark:bg-[#1a1a1a]/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 dark:border-[#374151]/50 overflow-hidden">
            <div class="bg-gradient-to-r from-[#667eea] to-[#764ba2] p-6">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-4">
                  <div class="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <i class="material-symbols-outlined text-white text-xl">edit_note</i>
                  </div>
                  <div>
                    <h1 class="text-xl font-bold text-white">{{ directT('ecommerce.editProduct.title') }}</h1>
                    <p class="text-white/80 text-sm">{{ product.title || directT('ecommerce.editProduct.subtitle') }}</p>
                  </div>
                </div>
                <div v-if="isExternalProduct" class="bg-amber-400/20 text-amber-100 px-4 py-2 rounded-xl text-sm font-medium border border-amber-300/30 backdrop-blur-sm">
                  <i class="material-symbols-outlined mr-2 text-sm">lock</i>
                  {{ directT('ecommerce.editProduct.readOnly') }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-20">
          <div class="relative">
            <div class="w-12 h-12 border-4 border-[#667eea]/20 border-t-[#667eea] rounded-full animate-spin"></div>
            <div class="absolute inset-0 w-12 h-12 border-4 border-transparent border-r-[#764ba2] rounded-full animate-spin" style="animation-delay: -0.5s;"></div>
          </div>
          <p class="mt-4 text-base font-medium text-[#667eea]">{{ directT('ecommerce.editProduct.loadingProduct') }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="bg-red-50/80 dark:bg-red-900/20 backdrop-blur-xl rounded-2xl shadow-lg border border-red-200/50 dark:border-red-800/30 p-6 mb-8">
          <div class="flex items-start gap-4">
            <div class="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center">
              <i class="material-symbols-outlined text-red-500 text-xl">error</i>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-red-700 dark:text-red-400">{{ directT('ecommerce.editProduct.errorLoadingProduct') }}</h3>
              <p class="text-red-600 dark:text-red-300 mt-1">{{ error }}</p>
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div v-else class="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          <!-- Left Column - Main Product Details -->
          <div class="lg:col-span-3 space-y-8">
            
            <!-- Basic Information -->
            <div class="bg-white/80 dark:bg-[#1a1a1a]/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 dark:border-[#374151]/50 overflow-hidden group hover:shadow-xl transition-all duration-300">
              <div class="bg-gradient-to-r from-[#4facfe] to-[#00f2fe] p-5">
                <h2 class="text-lg font-bold text-white flex items-center">
                  <i class="material-symbols-outlined mr-3 text-xl">info</i>
                  {{ directT('ecommerce.editProduct.basicInformation') }}
                </h2>
              </div>
              
              <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <!-- Product Title -->
                  <div class="md:col-span-2">
                    <label class="block text-sm font-semibold text-[#374151] dark:text-[#E2E8F0] mb-3">
                      {{ directT('ecommerce.editProduct.productTitle') }}
                    </label>
                    <input
                      type="text"
                      v-model="product.title"
                      :disabled="isExternalProduct"
                      class="w-full h-12 rounded-xl bg-gradient-to-r from-[#F8FAFF] to-[#F0F7FF] dark:from-[#0f172a] dark:to-[#1e293b] border border-[#E5E7EB]/50 dark:border-[#4B5563]/50 text-[#1F2937] dark:text-white px-4 text-sm outline-none focus:border-[#4facfe] focus:ring-2 focus:ring-[#4facfe]/20 transition-all duration-300 disabled:opacity-50 disabled:bg-gray-100 shadow-sm"
                      :placeholder="directT('ecommerce.editProduct.productTitlePlaceholder')"
                    />
                  </div>

                  <!-- Product Type -->
                  <div>
                    <label class="block text-sm font-semibold text-[#374151] dark:text-[#E2E8F0] mb-3">
                      {{ directT('ecommerce.editProduct.productType') }}
                    </label>
                    <select
                      v-model="product.product_type"
                      :disabled="isExternalProduct"
                      class="w-full h-12 rounded-xl bg-gradient-to-r from-[#F8FAFF] to-[#F0F7FF] dark:from-[#0f172a] dark:to-[#1e293b] border border-[#E5E7EB]/50 dark:border-[#4B5563]/50 text-[#1F2937] dark:text-white px-4 text-sm outline-none focus:border-[#4facfe] focus:ring-2 focus:ring-[#4facfe]/20 transition-all duration-300 disabled:opacity-50 disabled:bg-gray-100 cursor-pointer shadow-sm"
                    >
                      <option value="">{{ directT('ecommerce.editProduct.selectType') }}</option>
                      <option value="Digital Product">{{ directT('ecommerce.editProduct.digitalProduct') }}</option>
                      <option value="Physical Product">{{ directT('ecommerce.editProduct.physicalProduct') }}</option>
                      <option value="Service">{{ directT('ecommerce.createProduct.service') }}</option>
                    </select>
                  </div>

                  <!-- Category -->
                  <div>
                    <label class="block text-sm font-semibold text-[#374151] dark:text-[#E2E8F0] mb-3">
                      {{ directT('ecommerce.editProduct.category') }}
                    </label>
                    <input
                      type="text"
                      v-model="product.category"
                      :disabled="isExternalProduct"
                      class="w-full h-12 rounded-xl bg-gradient-to-r from-[#F8FAFF] to-[#F0F7FF] dark:from-[#0f172a] dark:to-[#1e293b] border border-[#E5E7EB]/50 dark:border-[#4B5563]/50 text-[#1F2937] dark:text-white px-4 text-sm outline-none focus:border-[#4facfe] focus:ring-2 focus:ring-[#4facfe]/20 transition-all duration-300 disabled:opacity-50 disabled:bg-gray-100 shadow-sm"
                      :placeholder="directT('ecommerce.editProduct.categoryPlaceholder')"
                    />
                  </div>

                  <!-- SKU -->
                  <div>
                    <label class="block text-sm font-semibold text-[#374151] dark:text-[#E2E8F0] mb-3">
                      {{ directT('ecommerce.editProduct.sku') }}
                    </label>
                    <input
                      type="text"
                      v-model="product.sku"
                      :disabled="isExternalProduct"
                      class="w-full h-12 rounded-xl bg-gradient-to-r from-[#F8FAFF] to-[#F0F7FF] dark:from-[#0f172a] dark:to-[#1e293b] border border-[#E5E7EB]/50 dark:border-[#4B5563]/50 text-[#1F2937] dark:text-white px-4 text-sm outline-none focus:border-[#4facfe] focus:ring-2 focus:ring-[#4facfe]/20 transition-all duration-300 disabled:opacity-50 disabled:bg-gray-100 shadow-sm"
                      :placeholder="directT('ecommerce.editProduct.skuPlaceholder')"
                    />
                  </div>

                  <!-- Product Link -->
                  <div>
                    <label class="block text-sm font-semibold text-[#374151] dark:text-[#E2E8F0] mb-3">
                      {{ directT('ecommerce.editProduct.productLink') }}
                    </label>
                    <input
                      type="url"
                      v-model="product.product_link"
                      :disabled="isExternalProduct"
                      class="w-full h-12 rounded-xl bg-gradient-to-r from-[#F8FAFF] to-[#F0F7FF] dark:from-[#0f172a] dark:to-[#1e293b] border border-[#E5E7EB]/50 dark:border-[#4B5563]/50 text-[#1F2937] dark:text-white px-4 text-sm outline-none focus:border-[#4facfe] focus:ring-2 focus:ring-[#4facfe]/20 transition-all duration-300 disabled:opacity-50 disabled:bg-gray-100 shadow-sm"
                      :placeholder="directT('ecommerce.editProduct.productLinkPlaceholder')"
                    />
                  </div>

                  <!-- Description -->
                  <div class="md:col-span-2">
                    <label class="block text-sm font-semibold text-[#374151] dark:text-[#E2E8F0] mb-3">
                      {{ directT('ecommerce.editProduct.description') }}
                    </label>
                    <textarea
                      v-model="product.description"
                      :disabled="isExternalProduct"
                      rows="5"
                      class="w-full rounded-xl bg-gradient-to-r from-[#F8FAFF] to-[#F0F7FF] dark:from-[#0f172a] dark:to-[#1e293b] border border-[#E5E7EB]/50 dark:border-[#4B5563]/50 text-[#1F2937] dark:text-white p-4 text-sm outline-none focus:border-[#4facfe] focus:ring-2 focus:ring-[#4facfe]/20 transition-all duration-300 disabled:opacity-50 disabled:bg-gray-100 resize-none shadow-sm"
                      :placeholder="directT('ecommerce.editProduct.descriptionPlaceholder')"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pricing Information -->
            <div class="bg-white/80 dark:bg-[#1a1a1a]/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 dark:border-[#374151]/50 overflow-hidden group hover:shadow-xl transition-all duration-300">
              <div class="bg-gradient-to-r from-[#fa709a] to-[#fee140] p-5">
                <h2 class="text-lg font-bold text-white flex items-center">
                  <i class="material-symbols-outlined mr-3 text-xl">payments</i>
                  {{ directT('ecommerce.editProduct.pricingInformation') }}
                </h2>
              </div>
              
              <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <!-- Regular Price -->
                  <div>
                    <label class="block text-sm font-semibold text-[#374151] dark:text-[#E2E8F0] mb-3">
                      {{ directT('ecommerce.editProduct.price') }}
                    </label>
                    <div class="relative">
                      <span class="absolute left-4 top-1/2 -translate-y-1/2 text-[#fa709a] font-bold">₪</span>
                      <input
                        type="number"
                        v-model="product.price"
                        :disabled="isExternalProduct"
                        class="w-full h-12 rounded-xl bg-gradient-to-r from-[#F8FAFF] to-[#F0F7FF] dark:from-[#0f172a] dark:to-[#1e293b] border border-[#E5E7EB]/50 dark:border-[#4B5563]/50 text-[#1F2937] dark:text-white pl-10 pr-4 text-sm outline-none focus:border-[#fa709a] focus:ring-2 focus:ring-[#fa709a]/20 transition-all duration-300 disabled:opacity-50 disabled:bg-gray-100 shadow-sm"
                        :placeholder="directT('ecommerce.editProduct.pricePlaceholder')"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <!-- Sale Price -->
                  <div>
                    <label class="block text-sm font-semibold text-[#374151] dark:text-[#E2E8F0] mb-3">
                      {{ directT('ecommerce.editProduct.salePrice') }}
                    </label>
                    <div class="relative">
                      <span class="absolute left-4 top-1/2 -translate-y-1/2 text-[#fa709a] font-bold">₪</span>
                      <input
                        type="number"
                        v-model="product.compare_at_price"
                        :disabled="isExternalProduct"
                        class="w-full h-12 rounded-xl bg-gradient-to-r from-[#F8FAFF] to-[#F0F7FF] dark:from-[#0f172a] dark:to-[#1e293b] border border-[#E5E7EB]/50 dark:border-[#4B5563]/50 text-[#1F2937] dark:text-white pl-10 pr-4 text-sm outline-none focus:border-[#fa709a] focus:ring-2 focus:ring-[#fa709a]/20 transition-all duration-300 disabled:opacity-50 disabled:bg-gray-100 shadow-sm"
                        :placeholder="directT('ecommerce.editProduct.salePricePlaceholder')"
                        step="0.01"
                      />
                    </div>
                  </div>



                  <!-- Publish Date -->
                  <div>
                    <label class="block text-sm font-semibold text-[#374151] dark:text-[#E2E8F0] mb-3">
                      {{ directT('ecommerce.editProduct.publishDate') }}
                    </label>
                    <input
                      type="date"
                      v-model="product.publish_date"
                      :disabled="isExternalProduct"
                      class="w-full h-12 rounded-xl bg-gradient-to-r from-[#F8FAFF] to-[#F0F7FF] dark:from-[#0f172a] dark:to-[#1e293b] border border-[#E5E7EB]/50 dark:border-[#4B5563]/50 text-[#1F2937] dark:text-white px-4 text-sm outline-none focus:border-[#fa709a] focus:ring-2 focus:ring-[#fa709a]/20 transition-all duration-300 disabled:opacity-50 disabled:bg-gray-100 shadow-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Product Image -->
            <div class="bg-white/80 dark:bg-[#1a1a1a]/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 dark:border-[#374151]/50 overflow-hidden group hover:shadow-xl transition-all duration-300">
              <div class="bg-gradient-to-r from-[#a8edea] to-[#fed6e3] p-5">
                <h2 class="text-lg font-bold text-gray-700 flex items-center">
                  <i class="material-symbols-outlined mr-3 text-xl">image</i>
                  {{ directT('ecommerce.editProduct.productImage') }}
                </h2>
              </div>
              
              <div class="p-6">
                <div class="flex items-start gap-6">
                  <!-- Current Image -->
                  <div v-if="product.image_link" class="w-24 h-24 rounded-2xl overflow-hidden border-2 border-white shadow-lg flex-shrink-0">
                    <img :src="product.image_link" alt="Product image" class="w-full h-full object-cover" />
                  </div>
                  
                  <!-- Upload Area -->
                  <div v-if="!isExternalProduct" class="flex-1">
                    <div class="relative flex items-center justify-center rounded-2xl py-8 px-6 border-2 border-dashed border-[#a8edea] hover:border-[#84d3d0] transition-all duration-300 bg-gradient-to-br from-[#a8edea]/10 to-[#fed6e3]/10 hover:from-[#a8edea]/20 hover:to-[#fed6e3]/20 group">
                      <div class="text-center">
                        <div class="w-16 h-16 bg-gradient-to-r from-[#a8edea] to-[#fed6e3] rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                          <i class="material-symbols-outlined text-gray-600 text-2xl">cloud_upload</i>
                        </div>
                        <p class="text-base font-semibold text-gray-700 mb-2">{{ directT('ecommerce.editProduct.clickToUpload') }}</p>
                        <p class="text-sm text-gray-500">{{ directT('ecommerce.editProduct.imageFormats') }}</p>
                      </div>
                      <input
                        type="file"
                        id="fileInput"
                        accept="image/*"
                        class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column - Sidebar -->
          <div class="space-y-8">
            
            <!-- Tags -->
            <div class="bg-white/80 dark:bg-[#1a1a1a]/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 dark:border-[#374151]/50 overflow-hidden group hover:shadow-xl transition-all duration-300">
              <div class="bg-gradient-to-r from-[#fbc2eb] to-[#a6c1ee] p-5">
                <h2 class="text-lg font-bold text-gray-700 flex items-center">
                  <i class="material-symbols-outlined mr-3 text-xl">label</i>
                  {{ directT('ecommerce.editProduct.tags') }}
                </h2>
              </div>
              
              <div class="p-6">
                <label class="block text-sm font-semibold text-[#374151] dark:text-[#E2E8F0] mb-3">
                  {{ directT('ecommerce.editProduct.productTags') }}
                </label>
                <input
                  type="text"
                  v-model="tagsInput"
                  :disabled="isExternalProduct"
                  class="w-full h-12 rounded-xl bg-gradient-to-r from-[#F8FAFF] to-[#F0F7FF] dark:from-[#0f172a] dark:to-[#1e293b] border border-[#E5E7EB]/50 dark:border-[#4B5563]/50 text-[#1F2937] dark:text-white px-4 text-sm outline-none focus:border-[#fbc2eb] focus:ring-2 focus:ring-[#fbc2eb]/20 transition-all duration-300 disabled:opacity-50 disabled:bg-gray-100 shadow-sm"
                  :placeholder="directT('ecommerce.editProduct.tagsPlaceholder')"
                />
                <p class="text-xs text-gray-500 mt-2">{{ directT('ecommerce.editProduct.tagsHint') }}</p>
                
                <!-- Tags Display -->
                <div v-if="parsedTags.length > 0" class="flex flex-wrap gap-2 mt-4">
                  <span 
                    v-for="(tag, index) in parsedTags" 
                    :key="index"
                    class="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-[#fbc2eb]/20 to-[#a6c1ee]/20 text-gray-700 border border-[#fbc2eb]/30 text-sm font-medium hover:from-[#fbc2eb]/30 hover:to-[#a6c1ee]/30 transition-all duration-200"
                  >
                    {{ tag }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div v-if="!isExternalProduct" class="bg-white/80 dark:bg-[#1a1a1a]/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 dark:border-[#374151]/50 overflow-hidden">
              <div class="p-6">
                <div class="space-y-4">
                  <!-- Save Button -->
                  <button 
                    type="submit"
                    :disabled="loading"
                    class="w-full h-12 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white font-semibold rounded-xl hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transform hover:scale-[1.02]"
                  >
                    <span v-if="loading" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    <i v-else class="material-symbols-outlined text-lg">save</i>
                    {{ loading ? directT('ecommerce.editProduct.saving') : directT('ecommerce.editProduct.saveChanges') }}
                  </button>

                  <!-- Cancel Button -->
                  <button 
                    type="button"
                    @click="$router.go(-1)"
                    class="w-full h-12 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 font-semibold rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-300 shadow-sm hover:shadow-md transform hover:scale-[1.02]"
                  >
                    <i class="material-symbols-outlined mr-2 text-lg">arrow_back</i>
                    {{ directT('ecommerce.editProduct.cancel') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Status -->
            <div class="bg-white/80 dark:bg-[#1a1a1a]/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 dark:border-[#374151]/50 overflow-hidden">
              <div class="bg-gradient-to-r from-[#84fab0] to-[#8fd3f4] p-5">
                <h3 class="text-lg font-bold text-gray-700 flex items-center">
                  <i class="material-symbols-outlined mr-3 text-xl">info</i>
                  {{ directT('ecommerce.editProduct.status') }}
                </h3>
              </div>
              <div class="p-6">
                <div class="space-y-4">
                  <div class="flex items-center justify-between">
                    <span class="text-sm font-medium text-gray-600">{{ directT('ecommerce.editProduct.status') }}</span>
                                          <span class="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-green-100 to-green-200 text-green-700 text-sm font-semibold border border-green-300">
                        <div class="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                        {{ directT('ecommerce.editProduct.active') }}
                      </span>
                  </div>
                  
                  <div class="flex items-center justify-between">
                    <span class="text-sm font-medium text-gray-600">{{ directT('ecommerce.editProduct.source') }}</span>
                                          <span class="text-sm font-semibold text-gray-800">
                        {{ isExternalProduct ? directT('ecommerce.editProduct.external') : directT('ecommerce.editProduct.manual') }}
                      </span>
                  </div>
                  
                  <div class="flex items-center justify-between">
                    <span class="text-sm font-medium text-gray-600">{{ directT('ecommerce.editProduct.lastUpdated') }}</span>
                    <span class="text-sm font-semibold text-gray-800">
                      {{ new Date().toLocaleDateString() }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { supabase } from "@/lib/supabaseClient";
import { useI18n } from 'vue-i18n';
import { createToast } from "@/utils/toast";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

interface Product {
  id?: string;
  title: string;
  description: string;
  product_type: string;
  sku: string;
  woocommerce_id: string;
  category: string;
  price: string;
  compare_at_price: string;
  product_link: string;
  image_link: string;
  publish_date: string;
  tags: string[];
  org_id?: string;
}

export default defineComponent({
  name: "EditProduct",
  setup() {
    const route = useRoute();
    const { t, locale } = useI18n();
    
    // Dynamic direction based on language
    const isRtl = computed(() => ['he', 'ar'].includes(locale.value));
    
    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };
    
    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };
    const productId = computed(() => route.query.id as string);
    const loading = ref(true);
    const error = ref<string | null>(null);
    const product = ref<Product>({
      title: "",
      description: "",
      product_type: "",
      sku: "",
      woocommerce_id: "",
      category: "",
      price: "",
      compare_at_price: "",
      product_link: "",
      image_link: "",
      publish_date: "",
      tags: [],
    });
    const tagsInput = ref("");

    // Check if product is from external platform and should be read-only
    const isExternalProduct = computed(() => {
      return !!product.value.sku || !!product.value.woocommerce_id;
    });

    // Converts array of tags to comma-separated string for input field
    const parsedTags = computed(() => {
      if (!tagsInput.value) return [];
      return tagsInput.value.split(",").map(tag => tag.trim()).filter(tag => tag);
    });

    // Load product data from the database
    const loadProduct = async () => {
      if (!productId.value) {
        error.value = directT('ecommerce.editProduct.noProductIdProvided');
        loading.value = false;
        return;
      }

      try {
        loading.value = true;
        error.value = null;

        const { data, error: fetchError } = await supabase
          .from("products")
          .select("*")
          .eq("id", productId.value)
          .single();

        if (fetchError) {
          throw fetchError;
        }

        if (!data) {
          throw new Error(directT('ecommerce.editProduct.productNotFound'));
        }

        // Convert database data to our Product interface with individual type assertions
        product.value = {
          id: String(data.id),
          title: data.title ? String(data.title) : "",
          description: data.description ? String(data.description) : "",
          product_type: data.product_type ? String(data.product_type) : "",
          sku: data.shopify_id ? String(data.shopify_id) : "",
          woocommerce_id: data.woocommerce_id ? String(data.woocommerce_id) : "",
          category: data.category ? String(data.category) : "",
          price: data.price ? String(data.price) : "",
          compare_at_price: data.compare_at_price ? String(data.compare_at_price) : "",
          product_link: data.product_link ? String(data.product_link) : "",
          image_link: data.image_link ? String(data.image_link) : "",
          publish_date: data.published_at ? new Date(String(data.published_at)).toISOString().split('T')[0] : "",
          tags: Array.isArray(data.tags) ? data.tags.map(tag => String(tag)) : [],
          org_id: data.org_id ? String(data.org_id) : undefined,
        };

        // Set tags input
        tagsInput.value = Array.isArray(data.tags) ? data.tags.map(tag => String(tag)).join(", ") : "";

        loading.value = false;
      } catch (err: any) {
        console.error("Error loading product:", err);
        error.value = `${directT('ecommerce.editProduct.failedToLoadProduct')}: ${err.message || directT('ecommerce.editProduct.unknownError')}`;
        loading.value = false;
      }
    };

    // Save product changes to the database
    const saveProduct = async () => {
      try {
        loading.value = true;
        error.value = null;

        // Convert product data to database format
        const updateData = {
          title: product.value.title,
          description: product.value.description,
          product_type: product.value.product_type,
          shopify_id: product.value.sku,
          woocommerce_id: product.value.woocommerce_id,
          category: product.value.category,
          price: product.value.price ? parseFloat(product.value.price) : null,
          compare_at_price: product.value.compare_at_price ? parseFloat(product.value.compare_at_price) : null,
          product_link: product.value.product_link,
          image_link: product.value.image_link,
          published_at: product.value.publish_date ? new Date(product.value.publish_date).toISOString() : null,
          tags: parsedTags.value,
          updated_at: new Date().toISOString(),
        };

        const { error: updateError } = await supabase
          .from("products")
          .update(updateData)
          .eq("id", productId.value);

        if (updateError) {
          throw updateError;
        }

        createToast(directT('ecommerce.editProduct.productUpdatedSuccessfully'), 'success');
        loading.value = false;
      } catch (err: any) {
        console.error("Error updating product:", err);
        error.value = `${directT('ecommerce.editProduct.failedToUpdateProduct')}: ${err.message || directT('ecommerce.editProduct.unknownError')}`;
        loading.value = false;
      }
    };

    onMounted(() => {
      loadProduct();
    });

    return {
      product,
      loading,
      error,
      tagsInput,
      parsedTags,
      saveProduct,
      isExternalProduct,
      directT,
      isRtl,
    };
  },
});
</script>
